import React, { useState } from 'react';
import * as echarts from 'echarts';
import ChatInterface from './ChatInterface';
import { Lock } from 'lucide-react';
import { LoginForm } from './Login';

const Header = ({ tab, setTab, setIsAuthenticated }) => {
  const activeClass = "cursor-pointer text-blue-400 font-medium border-b-2 border-blue-400 pb-1";
  const inactiveClass = "cursor-pointer text-gray-300 hover:text-white transition-colors";
  const handleLogout = () => {
    localStorage.removeItem("adminaccesstoken");
    setIsAuthenticated(false);
  };
  return (
    <header className="bg-slate-800 shadow-md fixed top-0 left-0 right-0 z-50">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <i className="fas fa-server text-blue-400 text-2xl"></i>
          <h1 className="text-xl font-bold text-white">Client Management Dashboard</h1>
        </div>
        <div className="flex items-center space-x-6">
          <nav className="hidden md:flex items-center space-x-6">
            <a 
              onClick={() => setTab('Clients')}
              className={tab === 'Clients' ? activeClass : inactiveClass}
            >
              Clients
            </a>
            <a 
              onClick={() => setTab('ManageClients')}
              className={tab === 'ManageClients' ? activeClass : inactiveClass}
            >
              Manage Clients
            </a>
          </nav>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded"
          >
            <Lock size={16} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-slate-800 py-6 mt-8">
    <div className="container mx-auto px-4">
      <div className="flex flex-col md:flex-row justify-between items-center">
        <div className="text-gray-400 text-sm mb-4 md:mb-0">
          © 2025 Client Management System. All rights reserved.
        </div>
        <div className="flex space-x-4">
          <a href="#" className="text-gray-400 hover:text-white">
            <i className="fab fa-github"></i>
          </a>
          <a href="#" className="text-gray-400 hover:text-white">
            <i className="fab fa-twitter"></i>
          </a>
          <a href="#" className="text-gray-400 hover:text-white">
            <i className="fab fa-linkedin"></i>
          </a>
        </div>
      </div>
    </div>
  </footer>
);

const DashboardStats = ({ clients }) => {
  const totalClients = clients.length;
  const activeClients = clients.filter(c => c.status === 'active').length;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 max-w-5xl mx-auto">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-gray-400 text-sm">Total Clients</p>
            <h3 className="text-3xl font-bold text-white mt-1">{totalClients}</h3>
          </div>
          <div className="bg-blue-500 bg-opacity-20 p-3 rounded-full">
            <i className="fas fa-building text-blue-400"></i>
          </div>
        </div>
        <div className="mt-4">
          <span className="text-green-400 text-sm flex items-center">
            <i className="fas fa-arrow-up mr-1"></i>
            12% <span className="text-gray-400 ml-2">from last month</span>
          </span>
        </div>
      </div>
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-gray-400 text-sm">Active Clients</p>
            <h3 className="text-3xl font-bold text-white mt-1">{activeClients}</h3>
          </div>
          <div className="bg-green-500 bg-opacity-20 p-3 rounded-full">
            <i className="fas fa-check-circle text-green-400"></i>
          </div>
        </div>
        <div className="mt-4">
          <span className="text-green-400 text-sm flex items-center">
            <i className="fas fa-arrow-up mr-1"></i>
            8% <span className="text-gray-400 ml-2">from last month</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const Modal = ({ title, message, onCancel, onConfirm, confirmText }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
      <h3 className="text-xl font-bold mb-4">{title}</h3>
      <p className="text-gray-300 mb-6">{message}</p>
      <div className="flex justify-end space-x-4">
        <button
          className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-button whitespace-nowrap cursor-pointer"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-button whitespace-nowrap cursor-pointer"
          onClick={onConfirm}
        >
          {confirmText}
        </button>
      </div>
    </div>
  </div>
);

const ScrapingModal = ({ baseUrl, maxDepth, maxUrls, onCancel, onConfirm }) => (
  <Modal
    title="Start URL Scraping"
    message={
      <>
        You are about to start scraping URLs from <span className="text-blue-400">{baseUrl}</span> with a maximum
        depth of <span className="text-blue-400">{maxDepth}</span> and a limit of <span className="text-blue-400">{maxUrls}</span> URLs.
        <br />
        This process may take some time. Do you want to continue?
      </>
    }
    onCancel={onCancel}
    onConfirm={onConfirm}
    confirmText="Start Scraping"
  />
);

const DataScrapingModal = ({ maxUrls, onCancel, onConfirm }) => (
  <Modal
    title="URL Scraping Complete"
    message={
      <>
        URL scraping has been completed successfully. Found <span className="text-blue-400">{Math.floor(maxUrls * 0.8)}</span> unique URLs.
        <br />
        Do you want to proceed with data scraping from these URLs?
      </>
    }
    onCancel={onCancel}
    onConfirm={onConfirm}
    confirmText="Start Data Scraping"
  />
);

const EmbeddingModal = ({ maxUrls, onCancel, onConfirm }) => (
  <Modal
    title="Data Scraping Complete"
    message={
      <>
        Data scraping has been completed successfully. Extracted <span className="text-blue-400">{Math.floor(maxUrls * 0.7)}</span> content items.
        <br />
        Do you want to proceed with generating embeddings for the scraped data?
      </>
    }
    onCancel={onCancel}
    onConfirm={onConfirm}
    confirmText="Generate Embeddings"
  />
);

const TabbedInterface = ({
  selectedTab,
  setSelectedTab,
  clientName,
  setClientName,
  username,
  setUsername,
  password,
  setPassword,
  addClient,
  selectedClient,
  setSelectedClient,
  baseUrl,
  setBaseUrl,
  maxDepth,
  setMaxDepth,
  maxUrls,
  setMaxUrls,
  scrapingStatus,
  scrapingProgress,
  dataScrapingProgress,
  embeddingProgress,
  startScraping,
  uploadedFile,
  setUploadedFile,
  handleFileChange,
  handleDrag,
  handleDrop,
  uploadDocument,
  clients,
  editStep,
  setEditStep,
  setShowScrapingConfirm,
  setShowDataScrapingConfirm,
  setShowEmbeddingConfirm
}) => {
  return (
    <div className="bg-slate-800 rounded-lg shadow-lg p-6 min-h-[600px] mb-8">
      <div className="flex border-b border-slate-700 mb-6">
        <button
          className={`py-2 px-4 font-medium text-sm whitespace-nowrap cursor-pointer ${selectedTab === 'addClient'
            ? 'text-blue-500 border-b-2 border-blue-500'
            : 'text-gray-400 hover:text-gray-300'
            }`}
          onClick={() => setSelectedTab('addClient')}
        >
          <i className="fas fa-plus-circle mr-2"></i>
          Add Client
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm whitespace-nowrap cursor-pointer ${selectedTab === 'client'
            ? 'text-blue-500 border-b-2 border-blue-500'
            : 'text-gray-400 hover:text-gray-300'
            }`}
          onClick={() => setSelectedTab('client')}
        >
          <i className="fas fa-building mr-2"></i>
          Edit Client
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm whitespace-nowrap cursor-pointer ${selectedTab === 'document'
            ? 'text-blue-500 border-b-2 border-blue-500'
            : 'text-gray-400 hover:text-gray-300'
            }`}
          onClick={() => setSelectedTab('document')}
        >
          <i className="fas fa-file-alt mr-2"></i>
          Add Document
        </button>
      </div>
      {selectedTab === 'addClient' && (
        <div>
          <h2 className="text-xl font-bold mb-4">Add New Client</h2>
          <form className="space-y-4">
            <div>
              <label htmlFor="clientName" className="block text-sm font-medium text-gray-300 mb-1">
                Client Name
              </label>
              <input
                type="text"
                id="clientName"
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter client name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                id="username"
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="pt-2">
              <button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-button font-medium transition-colors whitespace-nowrap cursor-pointer"
                onClick={addClient}
                disabled={!clientName || !username || !password}
              >
                <i className="fas fa-plus-circle mr-2"></i>
                Add Client
              </button>
            </div>
          </form>
        </div>
      )}
      {selectedTab === 'client' && (
        <>
          <h2 className="text-xl font-bold mb-4">Edit Client</h2>
          <form className="space-y-4">
            <div>
              <label htmlFor="selectClient" className="block text-sm font-medium text-gray-300 mb-1">
                Select Client
              </label>
              <div className="relative">
                <select
                  id="selectClient"
                  className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none appearance-none"
                  value={selectedClient || ''}
                  onChange={(e) =>
                    setSelectedClient(e.target.value ? parseInt(e.target.value) : null)
                  }
                >
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <i className="fas fa-chevron-down"></i>
                </div>
              </div>
            </div>
            {selectedClient && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">Select Step to Edit</label>
                <select
                  value={editStep || ""}
                  onChange={(e) => setEditStep(e.target.value)}
                  className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <option value="">-- Select Step --</option>
                  <option value="urlScraping">URL Scraping</option>
                  <option value="dataScraping">Data Scraping</option>
                  <option value="embedding">Embedding Generation</option>
                </select>
              </div>
            )}
            {selectedClient && editStep === "urlScraping" && (
              <>
                <div>
                  <label htmlFor="baseUrl" className="block text-sm font-medium text-gray-300 mb-1">
                    Base URL
                  </label>
                  <input
                    type="text"
                    id="baseUrl"
                    className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="https://example.com"
                    value={baseUrl}
                    onChange={(e) => setBaseUrl(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="maxDepth" className="block text-sm font-medium text-gray-300 mb-1">
                    Max Depth: {maxDepth}
                  </label>
                  <input
                    type="range"
                    id="maxDepth"
                    min="1"
                    max="10"
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    value={maxDepth}
                    onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>1</span>
                    <span>5</span>
                    <span>10</span>
                  </div>
                </div>
                <div>
                  <label htmlFor="maxUrls" className="block text-sm font-medium text-gray-300 mb-1">
                    Max URLs: {maxUrls}
                  </label>
                  <input
                    type="range"
                    id="maxUrls"
                    min="10"
                    max="1000"
                    step="10"
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    value={maxUrls}
                    onChange={(e) => setMaxUrls(parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>10</span>
                    <span>500</span>
                    <span>1000</span>
                  </div>
                </div>
                {scrapingStatus !== 'idle' && scrapingStatus !== 'completed' && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>URL Scraping Progress</span>
                        <span>{scrapingProgress}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2.5">
                        <div
                          className="bg-blue-500 h-2.5 rounded-full"
                          style={{ width: `${scrapingProgress}%` }}
                        ></div>
                      </div>
                    </div>
                    {(scrapingStatus === 'data_scraping' ||
                      scrapingStatus === 'embedding' ||
                      scrapingStatus === 'completed') && (
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Data Scraping Progress</span>
                          <span>{dataScrapingProgress}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2.5">
                          <div
                            className="bg-green-500 h-2.5 rounded-full"
                            style={{ width: `${dataScrapingProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    {(scrapingStatus === 'embedding' || scrapingStatus === 'completed') && (
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Embedding Generation Progress</span>
                          <span>{embeddingProgress}%</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2.5">
                          <div
                            className="bg-purple-500 h-2.5 rounded-full"
                            style={{ width: `${embeddingProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="pt-2">
                  {scrapingStatus === 'idle' && (
                    <button
                      type="button"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-button font-medium transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setShowScrapingConfirm(true)}
                      disabled={!selectedClient || !baseUrl}
                    >
                      <i className="fas fa-spider mr-2"></i>
                      Start URL Scraping
                    </button>
                  )}
                  {scrapingStatus === 'completed' && (
                    <div className="flex items-center justify-center text-green-400 mb-4">
                      <i className="fas fa-check-circle mr-2 text-xl"></i>
                      <span>Client updated successfully!</span>
                    </div>
                  )}
                </div>
              </>
            )}
            {selectedClient && editStep === "dataScraping" && (
              <>
                <div className="bg-slate-700 rounded-lg p-4">
                  Fetched URL Scraping Data: Base URL: {baseUrl}, Max Depth: {maxDepth}, Max URLs: {maxUrls}
                </div>
                <div className="pt-2">
                  <button
                    type="button"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-button font-medium transition-colors whitespace-nowrap cursor-pointer"
                    onClick={() => setShowDataScrapingConfirm(true)}
                  >
                    <i className="fas fa-sync-alt mr-2"></i>
                    Start Data Scraping
                  </button>
                </div>
              </>
            )}
            {selectedClient && editStep === "embedding" && (
              <>
                <div className="bg-slate-700 rounded-lg p-4">
                  Fetched Data Scraping Data: Data Scraping Progress: {dataScrapingProgress}%
                </div>
                <div className="pt-2">
                  <button
                    type="button"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-button font-medium transition-colors whitespace-nowrap cursor-pointer"
                    onClick={() => setShowEmbeddingConfirm(true)}
                  >
                    <i className="fas fa-cogs mr-2"></i>
                    Start Embedding Generation
                  </button>
                </div>
              </>
            )}
          </form>
        </>
      )}
      {selectedTab === 'document' && (
        <div>
          <h2 className="text-xl font-bold mb-4">Add Document for Client</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="clientSelect" className="block text-sm font-medium text-gray-300 mb-1">
                Select Client
              </label>
              <select
                id="clientSelect"
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={selectedClient || ''}
                onChange={(e) =>
                  setSelectedClient(e.target.value ? parseInt(e.target.value) : null)
                }
              >
                <option value="">Select a client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="documentTitle" className="block text-sm font-medium text-gray-300 mb-1">
                Document Title
              </label>
              <input
                type="text"
                id="documentTitle"
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter document title"
              />
            </div>
            <div>
              <label htmlFor="documentSource" className="block text-sm font-medium text-gray-300 mb-1">
                Source
              </label>
              <input
                type="text"
                id="documentSource"
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter document source"
              />
            </div>
            <div>
              <label htmlFor="documentDescription" className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                id="documentDescription"
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Enter document description"
              />
            </div>
            <div>
              <label htmlFor="documentText" className="block text-sm font-medium text-gray-300 mb-1">
                Document Text
              </label>
              <textarea
                id="documentText"
                rows={8}
                className="w-full bg-slate-700 border-none rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                placeholder="Enter document text content here..."
              ></textarea>
            </div>
            <div className="pt-2">
              <button
                type="button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-button font-medium transition-colors whitespace-nowrap cursor-pointer"
                onClick={uploadDocument}
                disabled={!selectedClient}
              >
                <i className="fas fa-save mr-2"></i>
                Save Document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const ManageClientsTable = ({ clients, searchTerm, setSearchTerm, deleteClient }) => {
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.baseUrl.toLowerCase().includes(searchTerm.toLowerCase())
  );
  return (
    <div className="bg-slate-800 rounded-lg shadow-lg p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h2 className="text-xl font-bold">Manage Clients</h2>
        <div className="mt-4 md:mt-0 relative">
          <input
            type="text"
            placeholder="Search clients..."
            className="bg-slate-700 border-none rounded-lg pl-10 pr-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none w-full md:w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-700">
          <thead className="bg-slate-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Base URL</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Documents</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Last Updated</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-slate-800 divide-y divide-slate-700">
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-400">
                  No clients found
                </td>
              </tr>
            ) : (
              filteredClients.map((client) => (
                <tr key={client.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">{client.name.charAt(0)}</span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">{client.name}</div>
                        <div className="text-sm text-gray-400">ID: {client.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    <a href={client.baseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      {client.baseUrl}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        client.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : client.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{client.documents.length}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{client.lastUpdated}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button title="View Client Details" className="text-blue-400 hover:text-blue-300 mr-3 cursor-pointer">
                      <i className="fas fa-eye"></i>
                    </button>
                    <button title="Edit Client" className="text-yellow-400 hover:text-yellow-300 mr-3 cursor-pointer">
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      title="Delete Client"
                      className="text-red-400 hover:text-red-300 cursor-pointer"
                      onClick={() => deleteClient(client.id)}
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-400">
          Showing <span className="font-medium">{filteredClients.length}</span> of{' '}
          <span className="font-medium">{clients.length}</span> clients
        </div>
        <div className="flex space-x-2">
          <button className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded-button text-sm whitespace-nowrap cursor-pointer">
            Previous
          </button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-button text-sm whitespace-nowrap cursor-pointer">
            1
          </button>
          <button className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded-button text-sm whitespace-nowrap cursor-pointer">
            2
          </button>
          <button className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1 rounded-button text-sm whitespace-nowrap cursor-pointer">
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [clientName, setClientName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [maxDepth, setMaxDepth] = useState(3);
  const [maxUrls, setMaxUrls] = useState(100);
  const [scrapingStatus, setScrapingStatus] = useState('idle');
  const [scrapingProgress, setScrapingProgress] = useState(0);
  const [dataScrapingProgress, setDataScrapingProgress] = useState(0);
  const [embeddingProgress, setEmbeddingProgress] = useState(0);
  const [showScrapingConfirm, setShowScrapingConfirm] = useState(false);
  const [showDataScrapingConfirm, setShowDataScrapingConfirm] = useState(false);
  const [showEmbeddingConfirm, setShowEmbeddingConfirm] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('addClient');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [editStep, setEditStep] = useState(null);
  const [clients, setClients] = useState([
    {
      id: 1,
      name: 'Example Corp',
      baseUrl: 'https://example.com',
      status: 'active',
      lastUpdated: '2025-04-05',
      documents: [
        { id: 1, name: 'Terms of Service.pdf', uploadDate: '2025-04-05', size: '2.3 MB' },
        { id: 2, name: 'Privacy Policy.docx', uploadDate: '2025-04-01', size: '1.5 MB' }
      ]
    },
    {
      id: 2,
      name: 'Tech Solutions',
      baseUrl: 'https://techsolutions.example',
      status: 'pending',
      lastUpdated: '2025-04-06',
      documents: [
        { id: 3, name: 'Product Documentation.pdf', uploadDate: '2025-04-06', size: '4.7 MB' }
      ]
    },
    {
      id: 3,
      name: 'Global Services',
      baseUrl: 'https://globalservices.com',
      status: 'error',
      lastUpdated: '2025-04-03',
      documents: []
    },
    {
      id: 4,
      name: 'InnovateX',
      baseUrl: 'https://innovatex.com',
      status: 'active',
      lastUpdated: '2025-04-07',
      documents: [
        { id: 4, name: 'Company Brochure.pdf', uploadDate: '2025-04-07', size: '3.2 MB' }
      ]
    }
  ]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [tab, setTab] = useState('Clients');
  const initialAuth = !!localStorage.getItem("adminaccesstoken");
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth);
  const addClient = () => {
    if (!clientName || !username || !password) {
      alert('Please fill in all required fields');
      return;
    }
    const newClient = {
      id: clients.length + 1,
      name: clientName,
      baseUrl: '',
      status: 'active',
      lastUpdated: new Date().toISOString().split('T')[0],
      documents: []
    };
    setClients(prev => [...prev, newClient]);
    setClientName('');
    setUsername('');
    setPassword('');
  };
  const startScraping = () => {
    if (!selectedClient || !baseUrl) {
      alert('Please select a client and provide a base URL');
      return;
    }
    setScrapingStatus('scraping');
    setScrapingProgress(0);
    const interval = setInterval(() => {
      setScrapingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setShowDataScrapingConfirm(true);
          return 100;
        }
        return prev + 5;
      });
    }, 300);
  };
  const startDataScraping = () => {
    setScrapingStatus('data_scraping');
    setDataScrapingProgress(0);
    setShowDataScrapingConfirm(false);
    const interval = setInterval(() => {
      setDataScrapingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setShowEmbeddingConfirm(true);
          return 100;
        }
        return prev + 4;
      });
    }, 250);
  };
  const startEmbedding = () => {
    setScrapingStatus('embedding');
    setEmbeddingProgress(0);
    setShowEmbeddingConfirm(false);
    const interval = setInterval(() => {
      setEmbeddingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setScrapingStatus('completed');
          return 100;
        }
        return prev + 3;
      });
    }, 200);
  };
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedFile(e.target.files[0]);
    }
  };
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadedFile(e.dataTransfer.files[0]);
    }
  };
  const uploadDocument = () => {
    if (!uploadedFile || selectedClient === null) {
      alert('Please select a client and upload a file');
      return;
    }
    const newDocId = Math.max(0, ...clients.flatMap(client => client.documents.map(doc => doc.id))) + 1;
    const updatedClients = clients.map(client => {
      if (client.id === selectedClient) {
        return {
          ...client,
          documents: [
            ...client.documents,
            {
              id: newDocId,
              name: uploadedFile.name,
              uploadDate: new Date().toISOString().split('T')[0],
              size: `${(uploadedFile.size / (1024 * 1024)).toFixed(1)} MB`
            }
          ]
        };
      }
      return client;
    });
    setClients(updatedClients);
    setUploadedFile(null);
    setSelectedClient(null);
  };
  const deleteClient = (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      setClients(clients.filter(client => client.id !== clientId));
    }
  };
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoginForm onLoginSuccess={() => setIsAuthenticated(true)} />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-slate-900 text-gray-100">
      <Header tab={tab} setTab={setTab} setIsAuthenticated={setIsAuthenticated}/>
      <main className="container mx-auto px-4 py-6 mt-16">
        {tab === 'Clients' ? (
          <ChatInterface />
        ) : (
          <>
            <DashboardStats clients={clients} />
            <TabbedInterface
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              clientName={clientName}
              setClientName={setClientName}
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              addClient={addClient}
              selectedClient={selectedClient}
              setSelectedClient={setSelectedClient}
              baseUrl={baseUrl}
              setBaseUrl={setBaseUrl}
              maxDepth={maxDepth}
              setMaxDepth={setMaxDepth}
              maxUrls={maxUrls}
              setMaxUrls={setMaxUrls}
              scrapingStatus={scrapingStatus}
              scrapingProgress={scrapingProgress}
              dataScrapingProgress={dataScrapingProgress}
              embeddingProgress={embeddingProgress}
              startScraping={startScraping}
              uploadedFile={uploadedFile}
              setUploadedFile={setUploadedFile}
              handleFileChange={handleFileChange}
              handleDrag={handleDrag}
              handleDrop={handleDrop}
              uploadDocument={uploadDocument}
              clients={clients}
              editStep={editStep}
              setEditStep={setEditStep}
              setShowScrapingConfirm={setShowScrapingConfirm}
              setShowDataScrapingConfirm={setShowDataScrapingConfirm}
              setShowEmbeddingConfirm={setShowEmbeddingConfirm}
            />
            <ManageClientsTable
              clients={clients}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              deleteClient={deleteClient}
            />
          </>
        )}
      </main>
      <Footer />
      {showScrapingConfirm && (
        <ScrapingModal
          baseUrl={baseUrl}
          maxDepth={maxDepth}
          maxUrls={maxUrls}
          onCancel={() => setShowScrapingConfirm(false)}
          onConfirm={startScraping}
        />
      )}
      {showDataScrapingConfirm && (
        <DataScrapingModal
          maxUrls={maxUrls}
          onCancel={() => {
            setShowDataScrapingConfirm(false);
            setScrapingStatus('idle');
          }}
          onConfirm={startDataScraping}
        />
      )}
      {showEmbeddingConfirm && (
        <EmbeddingModal
          maxUrls={maxUrls}
          onCancel={() => {
            setShowEmbeddingConfirm(false);
            setScrapingStatus('idle');
          }}
          onConfirm={startEmbedding}
        />
      )}
    </div>
  );
};

export default App;
